/**
 * Helper module for responsive behavior and breakpoint management.
 */

Promutuel.responsive = {
  /**
   * List of supported breakpoints. These must match the actual ones defined in CSS,
   * and they must be sorted from smaller to larger to work nicely with comparison operators.
   */
  BREAKPOINTS: {
    XS  : 0,
    SM  : 1,
    MD  : 2,
    LG  : 3,
    XL  : 4,
    XXL : 5,
    XXXL: 6,
  },

  /**
   * Computes the current window size, in terms of brekpoints.
   * The result can be compared to other breakpoints.
   * e.g.
   *
   * ```javascript
   * if (Promutuel.responsive.getCurrentSize() >= Promutuel.responsive.BREAKPOINTS.LG) {
   *  // Do something for the lg size.
   * }
   * ```
   *
   * @returns one of the constants in BREAKPOINTS.
   */
  getCurrentSize() {
    // Get the list of breakpoints in reverse order.
    const breakpoints = Object.entries(this.BREAKPOINTS)
      .sort((a, b) => a[1] - b[1])
      .map(entry => entry[0])
      .reverse();

    for (const bp of breakpoints) {
      const width = getComputedStyle(document.documentElement).getPropertyValue(`--breakpoint-${bp.toLowerCase()}`);

      if (window.matchMedia(`(min-width:${width})`).matches) return this.BREAKPOINTS[bp];
    }
    return -1;
  },

  /**
   * Enable viewport events on the element. After calling this,
   * `'breakpoint*'`events will be triggered automatically on the `window` object.
   * You can then use handlers attached with regular `addEventListener()`.
   *
   * The following events are supported:
   * - `breakpointchange`: the viewport just switched from one breakpoint to another one,
   * - `breakpointup`: triggered just after `breakpointchange` if the current breakpoint is larger than the previous one,
   * - `breakpointdown`: triggered just after `breakpointchange` if the current breakpoint is smaller than the previous one.
   *
   * All events will be dispatched with a `detail` property with the following properties:
   * - `from`: the previous breakpoint (a BREAKPOINTS constant),
   * - `to`  : the new breakpoint (a BREAKPOINTS constant).
   */
  enableBreakpointEvents() {
    window.addEventListener('resize', event => {
      // Initialize the variable only when the window is resized.
      if (currentSize === undefined) currentSize = Promutuel.responsive.getCurrentSize();

      const newSize = Promutuel.responsive.getCurrentSize();
      if (newSize != currentSize) {
        const detail = {
          from: currentSize,
          to: newSize,
        };
        window.dispatchEvent(new CustomEvent('breakpointchange', { detail }));
        if (newSize > currentSize) window.dispatchEvent(new CustomEvent('breakpointup', { detail }));
        else window.dispatchEvent(new CustomEvent('breakpointdown', { detail }));
      }
      currentSize = newSize;
    });
  }
}

// State variable for breakpoint event manager.
let currentSize = undefined;

// Always enable those events at page load.
Promutuel.responsive.enableBreakpointEvents();
